import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link } from 'react-router'


import { AuthLayout } from './AuthLayout'
import { Field, FormError, SubmitButton } from './Field'

import { useSignUp } from '@/lib/auth/queries'

const MIN_PASSWORD_LENGTH = 12

export function SignUpPage() {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const signUp = useSignUp()

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (passwordTooShort) return
    signUp.mutate({ email, password, fullName })
  }

  // Email verification is REQUIRED before a workspace can be created.
  if (signUp.isSuccess) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={`We sent a confirmation link to ${email}.`}
      >
        <p style={{ color: 'var(--color-text-secondary)' }}>
          Click the link to confirm your address, then sign in. The link expires in
          24 hours.
        </p>
        <p
          style={{
            marginTop: 'var(--spacing-4)',
            fontSize: 'var(--text-caption)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          Running locally? Mail is captured at{' '}
          <a href="http://localhost:54324">localhost:54324</a>.
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Turn every website visitor into a conversation."
      footer={
        <>
          Already have an account? <Link to="/auth/signin">Sign in</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        {signUp.error && <FormError message={signUp.error.message} />}

        <Field
          label="Full name"
          value={fullName}
          onChange={setFullName}
          autoComplete="name"
          required
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          disabled={signUp.isPending}
        />
        <Field
          label="Work email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          disabled={signUp.isPending}
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          error={passwordTooShort ? `Use at least ${MIN_PASSWORD_LENGTH} characters.` : undefined}
          disabled={signUp.isPending}
        />

        <SubmitButton loading={signUp.isPending} disabled={passwordTooShort}>
          Create account
        </SubmitButton>
      </form>
    </AuthLayout>
  )
}
